import { displayUnitOf } from "@heraion/shared";
import { useMemo, useState } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import { coerceLocale, type CoachAthleteCard } from "@heraion/shared";
import {
  AthleteDetail,
  ProgramPickerSheet,
  TargetsEditor,
  createSessionResultsCache,
  makeAthleteScopedApiFetch,
  resolveTheme,
  useAthleteDetail,
  useCatalogItems,
  useTargetsEditorSwap,
  type ProgressDeps,
} from "@heraion/ui";

import { apiFetch } from "./api";
import { exerciseCatalog } from "./logger/exercisesCache";
import { useAuth } from "./auth/AuthContext";
import { notifyViaWindow } from "./confirm";
import { scheduleIdle } from "./progress/scheduleIdle";
import { durableWrite } from "./durableWrite";
import { useThemePalette } from "./theme";
import { ProfileReadFailed } from "./ProfileLoadState";

// Thin web-rnw mount of the shared <AthleteDetail> (CO-9). The shared Progress
// panels inside it self-fetch through an ATHLETE-SCOPED ProgressDeps: apiFetch
// wrapped so the allowlisted reads hit the CO-11 edge-gated routes, plus a
// FRESH per-athlete results cache (the module-level Progress cache belongs to
// the coach's own athlete surface — sharing it would leak one partition's
// numbers into the other). No figure: the athlete's preferredFigure is outside
// the coach profile allowlist, and the coverage figure is optional.
//
// CO-7 R4: the targets editor is a SWAP inside this host — rendered OUTSIDE the
// ScrollView (its top bar stays sticky) and in place of <AthleteDetail>, keeping
// the SAME memoized `deps` so the warmed session-results cache survives the
// round-trip (a new route would drop it and spinner the reference numbers). The
// base (unscoped) apiFetch carries the ETag write-lock; the athlete-scoped one
// is read-only by construction and stays out of the write.
export function AthleteDetailScreen({
  card,
  onBack,
}: {
  card: CoachAthleteCard;
  onBack: () => void;
}) {
  const { dark, family } = useThemePalette();
  const theme = resolveTheme(dark, family);
  const { t, i18n } = useTranslation();
  const locale = coerceLocale(i18n.language);
  // The base (unscoped) client: this hook's reads (/api/profiles/:sub,
  // /api/me/programs, the MAX map) carry ?athleteSub themselves, and the
  // first two are NOT in athleteScopedApi's allowlist, so the scoped wrapper
  // would throw on them.
  const {
    loading,
    profile,
    profileFailed,
    reloadProfile,
    programs,
    maxes,
    athleteTargets: loadedTargets,
    sessions,
  } = useAthleteDetail(apiFetch, card.sub);

  // The detail ↔ editor swap + targets-refresh-on-save (shared with mobile).
  const { targets, editing, openEditor, closeEditor, onSaved } =
    useTargetsEditorSwap(loadedTargets);

  // CO-23 — the program/workout picker, mobile's exact contract minus the
  // hardware-back guard (no BackHandler on the web): a sheet rendered as a
  // sibling of the ScrollView, toggled by a plain flag — there is no draft to
  // lose, a selection commits immediately, so nothing here needs guarding.
  const [assigning, setAssigning] = useState(false);
  // The coach's own sub, straight off auth — NOT useCoachEdgesWeb, which
  // would re-fire the /api/me/coach-edges fetch this screen has no other use
  // for (CN-coach-edges-fetch already burned once on this exact duplicate).
  const { state: authState } = useAuth();
  const callerSub = authState.status === "signed_in" ? authState.user.sub : null;
  const catalog = useCatalogItems({ catalog: exerciseCatalog });

  const scopedApi = useMemo(
    () => makeAthleteScopedApiFetch(apiFetch, card.sub),
    [card.sub],
  );
  // Fresh single-flight cache per athlete — keyed on the sub so switching
  // athletes can never serve the previous athlete's snapshot.
  const resultsCache = useMemo(
    () => createSessionResultsCache(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [card.sub],
  );

  const units = displayUnitOf(profile);
  // #343: the targets editor writes in the athlete's unit, so it waits for the
  // read. A 404 (no profile yet) writes in kg; a failed read writes nothing.
  const writeUnits = loading || profileFailed ? null : units;
  const deps = useMemo<ProgressDeps>(
    () => ({
      apiFetch: scopedApi,
      theme,
      units,
      painMap: {},
      resultsCache,
      isDark: dark,
      weekStartDay: profile?.weekStartDay,
    }),
    [scopedApi, theme, units, resultsCache, dark, profile?.weekStartDay],
  );

  if (editing && writeUnits !== null) {
    return (
      <TargetsEditor
        theme={theme}
        apiFetch={apiFetch}
        deps={deps}
        source={{
          athleteSub: card.sub,
          onSaved,
        }}
        // On the coach's screen the athlete's own edits carry their name; every
        // other author is the coach reading it.
        resolveAuthor={(bySub) =>
          bySub === card.sub
            ? card.name
            : t("coach.targets.historyByYou", { defaultValue: "You" })
        }
        units={writeUnits}
        locale={locale}
        backLabel={card.name}
        onClose={closeEditor}
      />
    );
  }

  return (
    <>
      <ScrollView contentContainerStyle={styles.content}>
        {profileFailed ? (
          <View style={{ padding: theme.spacing.s4 }}>
            <ProfileReadFailed theme={theme} error="" onRetry={reloadProfile} />
          </View>
        ) : null}
        <AthleteDetail
          theme={theme}
          card={card}
          deps={deps}
          programs={programs}
          currentProgramId={profile?.currentProgramId}
          weekStartDay={profile?.weekStartDay}
          maxes={maxes}
          athleteTargets={targets}
          onBack={onBack}
          onEditTargets={openEditor}
          editTargetsHint={writeUnits === null ? t("profile.loadingHint") : undefined}
          onAssignProgram={() => setAssigning(true)}
          schedule={scheduleIdle}
          // #25: no onOpenSession, the section opens the session in place.
          sessions={sessions}
        />
      </ScrollView>
      {assigning ? (
        <ProgramPickerSheet
          theme={theme}
          visible
          apiFetch={apiFetch}
          durableWrite={durableWrite}
          catalog={catalog.raw ? [...catalog.raw] : null}
          callerSub={callerSub}
          athleteSub={card.sub}
          athleteName={card.name}
          notify={notifyViaWindow}
          onCancel={() => setAssigning(false)}
        />
      ) : null}
    </>
  );
}

const styles = StyleSheet.create({
  content: { paddingBottom: 48 },
});
